import React from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Spinner, Row, Col, Progress } from 'reactstrap';
import { getNatureEffect, getNatureStatClass } from '../../utils/natureUtils';
import './StatsModal.css';

export default function StatsModal({ isOpen, toggle, loading, data }) {
  // Calculate HP percentage for progress bar
  const hpPercentage = data && data.max_hp ? Math.round((data.current_hp / data.max_hp) * 100) : 0;
  const hpColor = hpPercentage > 60 ? 'success' : hpPercentage > 30 ? 'warning' : 'danger';
  
  // Get nature effect information - if nature is null, we'll show it as neutral
  const natureEffect = getNatureEffect(data?.nature);
  
  // Prepare stat normalization for mini bars (support both long and short keys)
  const atk = Number(data?.attack ?? data?.atk ?? 0);
  const def = Number(data?.defense ?? data?.def ?? 0);
  const spa = Number(data?.special_attack ?? data?.spa ?? 0);
  const spd = Number(data?.special_defense ?? data?.spd ?? 0);
  const spe = Number(data?.speed ?? data?.spe ?? 0);
  const statValues = { atk, def, spa, spd, spe };
  const maxStat = Math.max(1, ...Object.values(statValues));
  const statPercent = (val) => Math.min(100, Math.round((Number(val || 0) / maxStat) * 100));
  
  // Totals
  const ivTotal = ['iv_hp','iv_atk','iv_def','iv_spa','iv_spd','iv_spe']
    .reduce((s, k) => s + (Number(data?.[k]) || 0), 0);
  const evTotal = ['ev_hp','ev_atk','ev_def','ev_spa','ev_spd','ev_spe']
    .reduce((s, k) => s + (Number(data?.[k]) || 0), 0);
  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg" className="stats-modal">
      <ModalHeader toggle={toggle}>
        {loading
          ? 'Loading...'
          : data?.error
            ? 'Error'
            : data?.name || 'Stats'}
      </ModalHeader>
      <ModalBody>
        {loading && (
          <div className="text-center py-5">
            <Spinner />
          </div>
        )}

        {!loading && data?.error && (
          <p className="text-danger text-center">{data.error}</p>
        )}

        {!loading && data && !data.error && (
          <>
            <div className="text-center mb-4">
              <img
                src={`/sprites/${data.name}.png`}
                alt={data.name}
                className="stats-portrait"
                onError={e => { e.currentTarget.src = `/icon/${data.name}.png`; }}
              />
            </div>
            <Row>
              <Col md={6}>
                <div className="summary-card">
                  <p><strong>ID:</strong> {data.id}</p>
                  <p><strong>Slot:</strong> {data.slot}</p>
                  <p><strong>Level:</strong> {data.level}</p>
                  <p><strong>EXP:</strong> {data.totalexp}</p>
                  <p><strong>Item:</strong> {data.item}</p>
                </div>
              </Col>
              <Col md={6}>
                <p className="mb-1"><strong>Moves:</strong></p>
                <div className="moves-chips mb-3">
                  {[data.move1, data.move2, data.move3, data.move4].map((mv, i) => (
                    <div key={i} className={`move-chip ${!mv || mv === 'Empty' ? 'empty' : ''}`}>
                      <span className="move-slot">{i + 1}</span>
                      <span>{mv || 'Empty'}</span>
                    </div>
                  ))}
                </div>
                <div className="mb-2 stats-hp">
                  <p className="mb-1"><strong>HP:</strong></p>
                  <div className="stats-hp-track">
                    <div
                      className={`stats-hp-fill stats-hp--${hpColor}`}
                      style={{ width: `${hpPercentage}%` }}
                    />
                    <div className="stats-hp-text">{`${data.current_hp} / ${data.max_hp}`}</div>
                  </div>
                </div>
              </Col>
            </Row>
            <hr />
            <div className="mb-3">
              <h6 className="mb-2">Nature</h6>
              <div className="nature-card">
                <div>
                  <span className="nature-badge">{data.nature || "Neutral"}</span>
                </div>
                <div className="nature-info">{natureEffect.description}</div>
              </div>
            </div>
            
            <hr />
            <h6 className="mb-3">Battle Stats</h6>
            <Row className="mb-3">
              <Col md={6}>
                <div className="stats-statbar mb-2">
                  <div className={`stats-statbar-label ${getNatureStatClass(data.nature, 'atk')}`}>
                    <span>Attack</span>
                    <strong>{atk}</strong>
                    {natureEffect.increased === 'atk' && <span className="stat-delta plus">+10%</span>}
                    {natureEffect.decreased === 'atk' && <span className="stat-delta minus">-10%</span>}
                  </div>
                  <Progress value={statPercent(atk)} color="info" />
                </div>
                <div className="stats-statbar mb-2">
                  <div className={`stats-statbar-label ${getNatureStatClass(data.nature, 'spa')}`}>
                    <span>Sp. Attack</span>
                    <strong>{spa}</strong>
                    {natureEffect.increased === 'spa' && <span className="stat-delta plus">+10%</span>}
                    {natureEffect.decreased === 'spa' && <span className="stat-delta minus">-10%</span>}
                  </div>
                  <Progress value={statPercent(spa)} color="info" />
                </div>
                <div className="stats-statbar mb-2">
                  <div className={`stats-statbar-label ${getNatureStatClass(data.nature, 'spe')}`}>
                    <span>Speed</span>
                    <strong>{spe}</strong>
                    {natureEffect.increased === 'spe' && <span className="stat-delta plus">+10%</span>}
                    {natureEffect.decreased === 'spe' && <span className="stat-delta minus">-10%</span>}
                  </div>
                  <Progress value={statPercent(spe)} color="info" />
                </div>
              </Col>
              <Col md={6}>
                <div className="stats-statbar mb-2">
                  <div className={`stats-statbar-label ${getNatureStatClass(data.nature, 'def')}`}>
                    <span>Defense</span>
                    <strong>{def}</strong>
                    {natureEffect.increased === 'def' && <span className="stat-delta plus">+10%</span>}
                    {natureEffect.decreased === 'def' && <span className="stat-delta minus">-10%</span>}
                  </div>
                  <Progress value={statPercent(def)} color="info" />
                </div>
                <div className="stats-statbar mb-2">
                  <div className={`stats-statbar-label ${getNatureStatClass(data.nature, 'spd')}`}>
                    <span>Sp. Defense</span>
                    <strong>{spd}</strong>
                    {natureEffect.increased === 'spd' && <span className="stat-delta plus">+10%</span>}
                    {natureEffect.decreased === 'spd' && <span className="stat-delta minus">-10%</span>}
                  </div>
                  <Progress value={statPercent(spd)} color="info" />
                </div>
              </Col>
            </Row>
            <div className="totals-row mb-3">
              <div className="total-pill">
                <span className="dot" style={{ background: '#6ee7b7' }} />
                <strong>IV Total:</strong> {ivTotal}
              </div>
              <div className="total-pill">
                <span className="dot" style={{ background: '#93c5fd' }} />
                <strong>EV Total:</strong> {evTotal}
              </div>
            </div>
            
            <hr />
            <h6 className="mb-3">Individual Values (IVs)</h6>
            <Row className="mb-3">
              <Col md={4}>
                <p><strong>IV HP:</strong> {data.iv_hp ?? 0}</p>
                <p><strong>IV ATK:</strong> {data.iv_atk ?? 0}</p>
              </Col>
              <Col md={4}>
                <p><strong>IV DEF:</strong> {data.iv_def ?? 0}</p>
                <p><strong>IV SPA:</strong> {data.iv_spa ?? 0}</p>
              </Col>
              <Col md={4}>
                <p><strong>IV SPD:</strong> {data.iv_spd ?? 0}</p>
                <p><strong>IV SPE:</strong> {data.iv_spe ?? 0}</p>
              </Col>
            </Row>
            
            <hr />
            <h6 className="mb-3">Effort Values (EVs)</h6>
            <Row>
              <Col md={4}>
                <p><strong>EV HP:</strong> {data.ev_hp ?? 0}</p>
                <p><strong>EV ATK:</strong> {data.ev_atk ?? 0}</p>
              </Col>
              <Col md={4}>
                <p><strong>EV DEF:</strong> {data.ev_def ?? 0}</p>
                <p><strong>EV SPA:</strong> {data.ev_spa ?? 0}</p>
              </Col>
              <Col md={4}>
                <p><strong>EV SPD:</strong> {data.ev_spd ?? 0}</p>
                <p><strong>EV SPE:</strong> {data.ev_spe ?? 0}</p>
              </Col>
            </Row>
          </>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={toggle}>
          Close
        </Button>
      </ModalFooter>
    </Modal>
  );
}
